/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */
import { useEffect, useState } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import { If, Then, Else } from "react-if";
import { CloudAccountsSortFields, SortOrder } from "~/generated/graphql";
import { LoaderWrap } from "~/components/Loader";
import { NoData } from "~/components/NoData";
import { Pagination } from "~/components/Pagination";
import { getNoSearchResultsMessage } from "~/components/SearchInput";
import { TableWrapper } from "~/components/Tables";
import { useQueryState, useGetSearchPhrase } from "~/hooks";
import { isNotNil, isEmpty } from "~/tools";
import { useGetCloudAccounts } from "~/views/accounts/hooks/useGetCloudAccounts/useGetCloudAccounts";
import { CloudAccountCtxProvider } from "../CloudAccountProvider";
import { AccountQuery } from "../types/AccountQuery";
import { prepareCloudAccountsSearchFilter } from "./accountFilterBuilders";
import { columns, CloudAccount } from "./CloudAccountsColumns";
import { CloudAccountTableHeader } from "./CloudAccountsTableHeader";

export function CloudAccountsTable() {
  const [cloudAccountsIds, setCloudAccountsIds] = useState<string[]>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { queryState } = useQueryState<AccountQuery>();
  const { phrase, isSearchAllowed } = useGetSearchPhrase(
    queryState.searchPhrase
  );

  const { data, loading, count } = useGetCloudAccounts({
    page: queryState.page,
    limit: queryState.limit,
    sort: {
      field: queryState.field ?? CloudAccountsSortFields.Name,
      order: queryState.order ?? SortOrder.Asc,
    },
    filter: prepareCloudAccountsSearchFilter(queryState, phrase),
  });

  const cleanRowSelection = () => setRowSelection({});

  useEffect(() => {
    cleanRowSelection();
  }, [queryState]);

  return (
    <CloudAccountCtxProvider value={{ cleanRowSelection }}>
      <CloudAccountTableHeader
        cloudAccountsIds={cloudAccountsIds}
        resultsCount={count ?? 0}
      />
      <If condition={!isSearchAllowed}>
        <Then>
          <NoData hasIcon message={getNoSearchResultsMessage()} />
        </Then>
        <Else>
          <LoaderWrap loading={loading} inContent>
            <If condition={isEmpty(data)}>
              <Then>
                <NoData hasIcon message={"No accounts available."} />
              </Then>
              <Else>
                <TableWrapper<Partial<CloudAccount>>
                  loading={loading}
                  data={data.filter(isNotNil)}
                  columns={columns()}
                  rowSelection={rowSelection}
                  selectedRowsHandler={setCloudAccountsIds}
                  setRowSelection={setRowSelection}
                  stripped
                />
                <Pagination count={count} />
              </Else>
            </If>
          </LoaderWrap>
        </Else>
      </If>
    </CloudAccountCtxProvider>
  );
}
